import { useCallback, useEffect, useMemo, useState } from 'react'

import { Box } from '@factorialco/deprecated-design-system'
import { F0Alert, F0Dialog } from '@factorialco/f0-react'

import i18n from 'modules/core/lib/i18n'

import { EmployeeSelectorV2 } from 'modules/employees/components/EmployeeSelectorV2'
import type { EmployeeSelectorV2MultiSelectState } from 'modules/employees/components/EmployeeSelectorV2/types'
import { TrainingMembership } from 'modules/trainings/pages/Trainings/Revamp/TrainingDetail/Detail/types'

import { BulkSelection } from 'modules/trainings/components/Revamp/TrainingsDetail/ParticipantsOneDataTable/hooks/useBulkActionsConfig'

import { useCertificateDownload } from './useCertificateDownload'

export const ExportCertificates = ({
  onClose,
  trainingId,
  trainingMemberships,
  initialSelection,
  initialSelectedEmployeeIds,
}: {
  onClose: () => void
  trainingId: number
  trainingMemberships: TrainingMembership[]
  initialSelection?: BulkSelection
  initialSelectedEmployeeIds?: number[]
}) => {
  const isBulkMode = initialSelection !== undefined

  const allParticipantEmployeeIds = useMemo(
    () =>
      trainingMemberships
        .map((membership) => membership.employee?.id)
        .filter((id): id is number => id !== undefined),
    [trainingMemberships]
  )

  const [selectedEmployeeIds, setSelectedEmployeeIds] = useState<number[]>(
    initialSelectedEmployeeIds ?? []
  )

  const {
    fetchDocumentIds,
    downloadDocuments,
    documentIds,
    isFetching,
    isDownloading,
    hasNoCertificates,
    hasError,
  } = useCertificateDownload({ trainingId })

  useEffect(() => {
    if (isBulkMode && initialSelection) {
      fetchDocumentIds(initialSelection)
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const certificateCount = documentIds.length

  const buildSelectionFromEmployeeIds = useCallback(
    (employeeIds: number[]): BulkSelection => {
      const membershipIds = trainingMemberships
        .filter(
          (membership) =>
            membership.employee?.id !== undefined && employeeIds.includes(membership.employee.id)
        )
        .map((membership) => membership.id)
        .filter((id): id is number => id !== undefined)

      return { type: 'manual', ids: membershipIds }
    },
    [trainingMemberships]
  )

  const handleExport = useCallback(async () => {
    if (isBulkMode) {
      if (certificateCount > 0) {
        await downloadDocuments()
        onClose()
      }
    } else {
      const selection = buildSelectionFromEmployeeIds(selectedEmployeeIds)
      const ids = await fetchDocumentIds(selection)

      if (ids.length > 0) {
        await downloadDocuments(ids)
        onClose()
      }
    }
  }, [
    isBulkMode,
    certificateCount,
    buildSelectionFromEmployeeIds,
    selectedEmployeeIds,
    fetchDocumentIds,
    downloadDocuments,
    onClose,
  ])

  const handleSelectionChange = useCallback((newSelection: EmployeeSelectorV2MultiSelectState) => {
    if (newSelection.type === 'manual') {
      setSelectedEmployeeIds(newSelection.ids)
    }
  }, [])

  const hasSelection = selectedEmployeeIds.length > 0
  const showCertificateCount =
    isBulkMode && !isFetching && !hasNoCertificates && !hasError && certificateCount > 0
  const canExport = isBulkMode
    ? !isFetching && !isDownloading && certificateCount > 0
    : hasSelection && !isFetching && !isDownloading

  return (
    <F0Dialog
      isOpen
      onClose={onClose}
      title={i18n.t(
        'trainings.trainings_revamp.training_detail.employees_table.export_certificates'
      )}
      primaryAction={{
        label: i18n.t('common.export'),
        onClick: handleExport,
        disabled: !canExport,
        loading: isFetching || isDownloading,
      }}
      secondaryAction={{
        label: i18n.t('common.cancel'),
        onClick: onClose,
      }}
    >
      <Box gap="s32">
        <EmployeeSelectorV2
          multiple
          value={selectedEmployeeIds}
          onChange={isBulkMode ? () => {} : handleSelectionChange}
          forcedFilters={{
            employeeIds: allParticipantEmployeeIds,
          }}
          label={i18n.t(
            'trainings.trainings_revamp.training_detail.employees_table.select_participants'
          )}
          mandatory
          options={{
            disableSelectAll: true,
          }}
          disabled={isBulkMode}
        />
        {showCertificateCount && (
          <F0Alert
            title={i18n.tp(
              'trainings.trainings_revamp.training_detail.employees_table.certificates_download_count',
              { count: certificateCount }
            )}
            variant="info"
            description={i18n.t(
              'trainings.trainings_revamp.training_detail.employees_table.certificates_stored_description'
            )}
          />
        )}
        {hasNoCertificates && (
          <F0Alert
            title={i18n.t(
              'trainings.trainings_revamp.training_detail.employees_table.no_certificates_to_download'
            )}
            variant="warning"
            description={i18n.t(
              'trainings.trainings_revamp.training_detail.employees_table.no_certificates_to_download_description'
            )}
          />
        )}
        {hasError && (
          <F0Alert
            title={i18n.t(
              'trainings.trainings_revamp.training_detail.employees_table.export_certificates_error'
            )}
            variant="critical"
            description={i18n.t(
              'trainings.trainings_revamp.training_detail.employees_table.export_certificates_error_description'
            )}
          />
        )}
      </Box>
    </F0Dialog>
  )
}
